import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    TextField,
} from '@mui/material';
import moment from 'moment';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { RazorpayOrderReferenceModelEnums } from '../../enums/RazorpayOrderReferenceModelEnums';
import { RefundServiceV2 } from '../../services/RefundServiceV2';

interface InputProps {
    customerId: number;
}

const refundServiceV2 = new RefundServiceV2();

const CustomerWalletRefundsTab = (props: InputProps) => {
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [modelName, setModelName] = useState<RazorpayOrderReferenceModelEnums>(
        RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE,
    );
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [refundAmount, setRefundAmount] = useState('');
    const [isRefunding, setIsRefunding] = useState(false);

    const refreshData = (limit: number, offset: number) => {
        setIsLoading(true);
        refundServiceV2
            .getWalletRefundsForCustomer(props.customerId, modelName, limit, offset)
            .then((res: any) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    };

    useEffect(() => {
        refreshData(rowsPerPage, page * rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.customerId, modelName, rowsPerPage, page]);

    const openRefundDialog = (row: any) => {
        setSelectedRow(row);
        setRefundAmount('');
        setRefundDialogOpen(true);
    };

    const handleRefund = async () => {
        if (!selectedRow || !refundAmount) return;
        setIsRefunding(true);
        try {
            await refundServiceV2.createCustomerWalletRefund(props.customerId, {
                referenceId: selectedRow.referenceId,
                referenceModelName: selectedRow.referenceModelName,
                refundAmount,
            });
            setRefundDialogOpen(false);
            refreshData(rowsPerPage, page * rowsPerPage);
        } catch {
            alert('Refund failed');
        } finally {
            setIsRefunding(false);
        }
    };

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        { label: 'Reference Id', field: 'referenceId' },
        { label: 'Reference Model', field: 'referenceModelName' },
        { label: 'Refunded Amount', field: 'amount' },
        {
            label: 'Total Amount',
            render: (row) =>
                row?.referenceObject?.amount != null
                    ? Math.abs(Number(row.referenceObject.amount)).toFixed(2)
                    : '-',
        },
        {
            label: 'Remaining Amount',
            render: (row) =>
                row?.referenceObject?.amount != null
                    ? (
                          Math.abs(Number(row.referenceObject.amount)) -
                          Number(row.referenceObject.amountRefunded || 0)
                      ).toFixed(2)
                    : '-',
        },
        { label: 'Refund Type', field: 'refundType' },
        { label: 'Status', field: 'status', type: 'status' },
        { label: 'Refunded By', render: (row) => row?.refundedBy ?? '-' },
        {
            label: 'Created At',
            render: (row) => moment(row?.createdAt).format('YYYY/MM/DD HH:mm'),
        },
        {
            label: 'Updated At',
            render: (row) => moment(row?.updatedAt).format('YYYY/MM/DD HH:mm'),
        },
        {
            label: 'Action',
            stopRowClick: true,
            render: (row) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => openRefundDialog(row)}
                    disabled={
                        row?.refundType === 'FULL' ||
                        (row?.referenceObject?.amount != null &&
                            Math.abs(Number(row.referenceObject.amount)) -
                                Number(row.referenceObject.amountRefunded || 0) <=
                                0)
                    }
                >
                    Refund
                </Button>
            ),
        },
    ];

    return (
        <Box pt={3}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={modelName}
                    onChange={(_, v) => {
                        setModelName(v);
                        setPage(0);
                    }}
                >
                    <Tab label="One Meal" value={RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE} />
                    <Tab label="Coupon" value={RazorpayOrderReferenceModelEnums.DISCOUNT_COUPON_ORDER} />
                    <Tab label="Chef for Party" value={RazorpayOrderReferenceModelEnums.CHEF_FOR_PARTY} />
                </Tabs>
            </Box>

            <Box mt={2}>
            <CommonListTable
                columns={columns}
                data={data}
                loading={isLoading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => {
                    setRowsPerPage(rows);
                    setPage(0);
                }}
                emptyText="No Wallet Refunds Found"
            />
            </Box>

            <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)}>
                <DialogTitle>Create Wallet Refund</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Refund Amount"
                        type="number"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundDialogOpen(false)} disabled={isRefunding}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRefund}
                        variant="contained"
                        disabled={isRefunding || !refundAmount}
                    >
                        {isRefunding ? 'Processing...' : 'Refund'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomerWalletRefundsTab;
